import { StandardEvents, CartLinesUpdateEvent, CartViewEvent } from '@shopify/events';
/** Remove retired automatic gifts while preserving optional paid accessories. */
let queue = Promise.resolve();
let queued = 0;
const pendingMutations = new Set();
let paidAddonQueue = Promise.resolve();
let paidAddonPending = 0;
let cartMutationVersion = 0;
const renderedCartSummaries = new WeakMap();
export const MILU_PAID_ADDON_PENDING_EVENT = 'milu:paid-addon-pending';
/** Register before an async preflight; the later native event may register the same promise safely. */
export function trackMiluCartMutation(promise) {
  if (!pendingMutations.has(promise)) cartMutationVersion++;
  pendingMutations.add(promise);
  const release = () => pendingMutations.delete(promise);
  promise.then(release, release);
}
// Include the original add/change request, not just the later gift reconciliation.
for (const type of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(type, event => {
    if (!event.promise) return;
    trackMiluCartMutation(event.promise);
  });
}
async function jsonRequest(url, body) {
  const response = await fetch(url, body ? {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)} : {headers:{Accept:'application/json'}});
  const data = await response.json();
  if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart update failed');
  return data;
}

/** Reuse the native view summary without adding the cart token to rendered markup. */
export function getMiluCartSummary(cart) {
  return renderedCartSummaries.get(cart) ?? CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
}

/** Read only the full cart rendered by this add response, never a cached/live DOM snapshot. */
function cartFromAddSections(sections) {
  if (!sections || typeof DOMParser === 'undefined') return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('data-milu-cart-state')) continue;
    try {
      const component = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-items-component');
      const cart = JSON.parse(component?.querySelector('[data-milu-cart-state]')?.textContent || 'null');
      const summary = JSON.parse(component?.getAttribute('view-event-payload') || 'null')?.cart;
      if (!cart || !summary || typeof cart.currency !== 'string' ||
        !Number.isInteger(cart.total_price) || cart.total_price < 0 ||
        !Number.isInteger(cart.item_count) || cart.item_count < 0 || !Array.isArray(cart.items) ||
        !Array.isArray(summary.lines) || summary.lines.length !== cart.items.length ||
        summary.totalQuantity !== cart.item_count || summary.cost?.totalAmount?.currencyCode !== cart.currency ||
        !Number.isFinite(Number(summary.cost.totalAmount.amount)) ||
        Math.abs(Number(summary.cost.totalAmount.amount) * 100 - cart.total_price) > 0.01) continue;
      if (cart.items.some((item, index) => !item || !item.variant_id || !item.key ||
        !Number.isInteger(item.quantity) || item.quantity <= 0 ||
        !Number.isInteger(item.final_line_price) || item.final_line_price < 0 ||
        !item.properties || typeof item.properties !== 'object' ||
        summary.lines[index]?.quantity !== item.quantity || !summary.lines[index]?.id ||
        summary.lines[index]?.cost?.totalAmount?.currencyCode !== cart.currency ||
        !Number.isFinite(Number(summary.lines[index]?.cost?.totalAmount?.amount)) ||
        Math.abs(Number(summary.lines[index].cost.totalAmount.amount) * 100 - item.final_line_price) > 0.01) ||
        cart.items.reduce((sum, item) => sum + item.quantity, 0) !== cart.item_count) continue;
      // CartViewEvent resolves the existing session ID using Shopify's own cookie handling.
      // Construction alone does not dispatch an extra analytics/view event.
      const eventCart = new CartViewEvent({ context: 'dialog', cart: summary }).cart;
      if (!eventCart?.id || !Array.isArray(eventCart.discountCodes)) continue;
      cart.discount_codes = eventCart.discountCodes;
      cart.sections = sections;
      renderedCartSummaries.set(cart, eventCart);
      return cart;
    } catch { /* Invalid/partial section rendering must keep the fresh cart GET fallback. */ }
  }
  return null;
}

/** Add responses contain only added lines; their rendered sections contain the complete cart. */
export async function resolveMiluAddedCart(response, mutationVersion) {
  if (mutationVersion === cartMutationVersion) {
    const rendered = cartFromAddSections(response.sections);
    if (rendered) return rendered;
  }
  // Never reuse a GET started before this mutation, or mix newer data with older section HTML.
  const cart = await jsonRequest(`${Theme.routes.cart_url}.js`);
  if (mutationVersion === cartMutationVersion) cart.sections = response.sections;
  return cart;
}

/** Capture before dispatching the caller's cart event, so a mutation never waits on itself. */
export function snapshotMiluCartUpdates() {
  return [...pendingMutations, queue, paidAddonQueue];
}

export function hasPendingMiluPaidAddon() {
  return paidAddonPending > 0;
}

export function hasPendingMiluCartUpdates() {
  return pendingMutations.size > 0 || queued > 0 || paidAddonPending > 0;
}

export function getMiluCartMutationVersion() {
  return cartMutationVersion;
}

function notifyPaidAddonPending() {
  document.dispatchEvent(new CustomEvent(MILU_PAID_ADDON_PENDING_EVENT, { detail: { pending: paidAddonPending } }));
}

export function waitForMiluPaidAddonUpdates() {
  return paidAddonQueue;
}

async function withCartSections(cart, sectionIds) {
  if (sectionIds.some(id => !cart.sections?.[id])) {
    const url = new URL(Theme.routes.cart_url, location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    cart.sections = await jsonRequest(url.toString());
  }
  return cart;
}

/**
 * The checkbox edits only accessories associated with this bundle variant. Historical and
 * separately purchased accessories have no marker and must never be removed by this switch.
 * @param {{bundleVariantId: string, addonVariantId: string, checked: boolean}} selection
 * @param {string[]} sections
 * @param {Promise<unknown>[]} priorMutations
 * @param {Function} beforeMutation - Dispatch the native event once real line keys are known.
 */
export function syncMiluPaidAddon(selection, sections, priorMutations, beforeMutation) {
  paidAddonPending++;
  notifyPaidAddonPending();
  const sectionIds = [...new Set(sections)].filter(Boolean).slice(0, 5);
  const task = paidAddonQueue.catch(() => {}).then(async () => {
    await Promise.allSettled(priorMutations);
    const rendering = sectionIds.length ? { sections: sectionIds, sections_url: location.pathname } : {};
    // Do not reuse an in-flight cart GET from before the preceding mutation.
    let cart = await jsonRequest(`${Theme.routes.cart_url}.js`);
    const eligible = cart.items.some(item => String(item.variant_id) === selection.bundleVariantId && item.quantity > 0);
    const associated = cart.items.filter(item => String(item.variant_id) === selection.addonVariantId &&
      item.properties?._milu_addon_for === selection.bundleVariantId);
    let changed = false;

    if (eligible && selection.checked && associated.length === 0) {
      beforeMutation({ action: 'add', lines: [{ merchandiseId: selection.addonVariantId, quantity: 1 }] });
      const mutationVersion = cartMutationVersion;
      const added = await jsonRequest(Theme.routes.cart_add_url, {
        items: [{ id: Number(selection.addonVariantId), quantity: 1,
          properties: { _milu_addon_for: selection.bundleVariantId } }], ...rendering,
      });
      cart = await resolveMiluAddedCart(added, mutationVersion);
      changed = true;
    } else if (eligible && !selection.checked && associated.length) {
      beforeMutation({ action: 'remove', lines: associated.map(item => ({ id: item.key, quantity: 0 })) });
      cart = await jsonRequest(Theme.routes.cart_update_url, {
        updates: Object.fromEntries(associated.map(item => [item.key, 0])), ...rendering,
      });
      changed = true;
    }

    // No-op selections have no native cart event and need no extra section request.
    return { cart: changed ? await withCartSections(cart, sectionIds) : cart, eligible };
  }).catch(async error => {
    // A lost response does not prove a POST failed. Recover actual quantities before rollback.
    try {
      error.cart = await withCartSections(await jsonRequest(`${Theme.routes.cart_url}.js`), sectionIds);
    } catch { /* Keep the original error when the connection is still unavailable. */ }
    throw error;
  }).finally(() => { paidAddonPending--; notifyPaidAddonPending(); });
  paidAddonQueue = task;
  return task;
}
export function syncMiluGift(initial, sections = []) {
  const wasQueued = queued++ > 0;
  const run = async () => {
    const config = JSON.parse(document.getElementById('milu-gift-config')?.textContent || '{}');
    if (!config.variant) return initial;
    const sectionIds = [...new Set(sections)].filter(Boolean).slice(0, 5);
    const rendering = sectionIds.length ? {sections: sectionIds, sections_url: location.pathname} : {};
    // Change/update responses already contain a full cart. Add callers pass their fresh cart read.
    let cart = !wasQueued && initial?.currency && Array.isArray(initial.items)
      ? initial : await jsonRequest(`${Theme.routes.cart_url}.js`);
    // Retire legacy automatic gifts; paid add-ons have no legacy gift property.
    const managed = cart.items.filter(i => i.properties?._milu_gift === 'threshold-60');
    if (managed.length) {
      cart = await jsonRequest(Theme.routes.cart_update_url, {
        updates: Object.fromEntries(managed.map(i => [i.key, 0])), ...rendering
      });
    }
    if (sectionIds.some(id => !cart.sections?.[id])) {
      cart = await jsonRequest(Theme.routes.cart_update_url,{updates:{},...rendering});
    }
    return cart;
  };
  const task=queue.catch(()=>{}).then(run).finally(()=>{queued--;});
  queue=task;
  return task;
}
// Wait for the original mutations and their already-queued cleanup before native checkout.
async function waitForCartUpdates() {
  while (true) {
    while (pendingMutations.size) await Promise.all([...pendingMutations]);
    const currentQueue = queue;
    await currentQueue;
    if (pendingMutations.size === 0 && queue === currentQueue) return;
  }
}

let submitting = false;
let preparingCheckout = false;
document.addEventListener('submit', async event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' || submitting) return;
  if (event.submitter?.name !== 'checkout') return;
  // Preserve the browser's original submission when nothing needs to finish first.
  // Re-submitting within the same submit task can be ignored by native re-entry guards.
  if (!preparingCheckout && pendingMutations.size === 0 && queued === 0 && !document.querySelector('.milu-cart-gift')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (preparingCheckout) return;

  preparingCheckout = true;
  const button = event.submitter;
  button.disabled = true;
  try {
    await waitForCartUpdates();
    // Normal carts need no extra network request. Old cart sessions still remove their retired gift.
    if (document.querySelector('.milu-cart-gift')) {
      await syncMiluGift(null);
      await waitForCartUpdates();
    }
    // Leave the original submit task before invoking requestSubmit again.
    await new Promise(resolve => setTimeout(resolve, 0));
    submitting = true;
    button.disabled = false;
    form.requestSubmit(button);
  } catch (error) {
    button.disabled = false;
    const message = document.querySelector('[data-milu-gift-error]');
    if (message) { message.hidden = false; message.focus(); }
    console.error('[MiluPets cart]', error);
  } finally {
    submitting = false;
    preparingCheckout = false;
  }
}, true);
